import { getAuthApiUrl, getDataApiUrl, getRealtimeUrl } from '../config/env';
import type { Show, HmsTokenResponse, Item, ItemSale, ItemSaleWithPayment, Order, OrderStatus, Shipment, ShipmentStatus, Listing, CreateListingRequest, UpdateListingRequest, CreateItemFromListingRequest, ShippingConfig, UpdateShippingConfigRequest, CarrierServiceOption, BuyerAddress, CreateBuyerAddressRequest, UpdateBuyerAddressRequest, BinSale, CreateBinSaleRequest, PaymentMethod, SetupIntentResponse, StripeConnectStatus, StripeOnboardingResponse, StripeDashboardLinkResponse, SellerLedgerEntry, SellerBalanceSummary, SellerPayout, UpdateTrackingRequest, GetRatesResponse, BuyLabelResponse, User, UpdateProfileRequest, NotificationsPage, Giveaway, CarrierPackage } from '../types';

export const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.snagg.live';

const ACCESS_TOKEN_KEY = 'auction_access_token';
const REFRESH_TOKEN_KEY = 'auction_refresh_token';
const USER_KEY = 'auction_user';

// Token refresh state - prevents concurrent refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

// Check if a JWT token is expired (with configurable buffer)
function isTokenExpired(token: string, bufferMs: number = 30000): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiry = payload.exp * 1000; // Convert to milliseconds
    return Date.now() >= expiry - bufferMs;
  } catch {
    return true; // If we can't parse it, treat as expired
  }
}

// Get token expiry time in ms
function getTokenExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000;
  } catch {
    return null;
  }
}

// Clear all auth state
function clearAuthState(): void {
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

// Perform token refresh - with mutex to prevent concurrent refreshes
async function performTokenRefresh(): Promise<string | null> {
  // If already refreshing, wait for that to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    clearAuthState();
    return null;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response = await fetch(`${getAuthApiUrl()}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        clearAuthState();
        return null;
      }

      const data = await response.json();
      sessionStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));

      return data.accessToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      clearAuthState();
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// Ensure we have a valid token before making a request
// This is proactive - refreshes BEFORE the request if token is expired/expiring
async function ensureValidToken(): Promise<string | null> {
  const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);

  // No token at all - try to refresh if we have a refresh token
  if (!token) {
    return performTokenRefresh();
  }

  // Token exists but is expired or expiring soon (within 30s)
  if (isTokenExpired(token, 30000)) {
    return performTokenRefresh();
  }

  return token;
}

// Error class that preserves HTTP status and response data
export class ApiError extends Error {
  status: number;
  data: Record<string, any>;

  constructor(message: string, status: number, data: Record<string, any> = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// Authenticated fetch wrapper with proactive token refresh
async function authFetch<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  // Proactively ensure we have a valid token before the request
  const token = await ensureValidToken();

  if (!token) {
    throw new Error('Session expired. Please log in again.');
  }

  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(url, { ...options, headers });

  // Handle auth errors (401 Unauthorized or 403 Forbidden)
  // This is a fallback in case the proactive check missed something
  if (response.status === 401 || response.status === 403) {
    // Try to refresh the token
    const newToken = await performTokenRefresh();

    if (!newToken) {
      throw new Error('Session expired. Please log in again.');
    }

    // Retry the request with new token
    const retryResponse = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${newToken}`,
      },
    });

    if (!retryResponse.ok) {
      // If still failing after refresh, check if it's another auth error
      if (retryResponse.status === 401 || retryResponse.status === 403) {
        clearAuthState();
        throw new Error('Session expired. Please log in again.');
      }
      const error = await retryResponse.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || 'Request failed');
    }

    const retryContentLength = retryResponse.headers.get('content-length');
    if (retryResponse.status === 204 || retryContentLength === '0') {
      return undefined as T;
    }

    return retryResponse.json();
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new ApiError(errorData.message || 'Request failed', response.status, errorData);
  }

  // Handle empty responses (204 No Content or empty body)
  const contentLength = response.headers.get('content-length');
  if (response.status === 204 || contentLength === '0') {
    return undefined as T;
  }

  return response.json();
}

// Export helper for getting access token (used by WebSocket hooks)
export function getAccessToken(): string | null {
  return sessionStorage.getItem(ACCESS_TOKEN_KEY);
}

// Refresh the access token if needed, returns valid token or null
// Exported for use by AuthContext and other components
export async function ensureFreshToken(): Promise<string | null> {
  return ensureValidToken();
}

// Check if currently authenticated (has valid-looking tokens)
export function hasValidAuth(): boolean {
  const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

  // Has a non-expired access token
  if (token && !isTokenExpired(token, 0)) {
    return true;
  }

  // Has a refresh token (can potentially refresh)
  return !!refreshToken;
}

// Get time until token expires (for scheduling refreshes)
export function getTimeUntilExpiry(): number | null {
  const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
  if (!token) return null;

  const expiry = getTokenExpiry(token);
  if (!expiry) return null;

  return Math.max(0, expiry - Date.now());
}

// Manual token refresh (for use by visibility change handler, etc.)
export async function refreshToken(): Promise<boolean> {
  const token = await performTokenRefresh();
  return token !== null;
}


// Password Reset API (unauthenticated)
export async function requestPasswordReset(phoneNumber: string): Promise<void> {
  const response = await fetch(`${getAuthApiUrl()}/api/v1/auth/request-password-reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phoneNumber }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }
}

export async function resetPassword(phoneNumber: string, code: string, newPassword: string): Promise<void> {
  const response = await fetch(`${getAuthApiUrl()}/api/v1/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phoneNumber, code, newPassword }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }
}

// Item API
export async function createItem(showId: string, item: Omit<Item, 'id'>): Promise<Item> {
  return authFetch<Item>(`${getDataApiUrl()}/api/v1/shows/${showId}/items`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: item.name,
      description: item.description,
      imageUrl: item.imageUrl,
      startingPrice: item.startingPrice,
    }),
  });
}

export async function listItems(showId: string): Promise<Item[]> {
  const response = await fetch(`${getDataApiUrl()}/api/v1/shows/${showId}/items`);
  if (!response.ok) throw new Error('Failed to list items');
  return response.json();
}

// Core Platform API (Java)
export async function createShow(
  name: string,
  thumbnailUrl?: string,
  scheduledAt?: Date,
  startingItemNumber?: number
): Promise<Show> {
  return authFetch<Show>(`${getDataApiUrl()}/api/v1/shows`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name,
      scheduledAt: (scheduledAt || new Date()).toISOString(),
      thumbnailUrl: thumbnailUrl || null,
      ...(startingItemNumber != null && { startingItemNumber }),
    }),
  });
}

export interface RecurrenceConfig {
  repeatType: 'DAILY' | 'WEEKLY';
  intervalDays?: number;
  weekdaysOnly?: boolean;
  intervalWeeks?: number;
  selectedDays?: string[];
  stopType: 'COUNT' | 'DATE';
  stopAfterCount?: number;
  stopAfterDate?: string; // ISO date string
}

export interface ScheduledShowsResponse {
  count: number;
  shows: Show[];
}

export async function createScheduledShows(
  name: string,
  firstShowAt: Date,
  recurrence: RecurrenceConfig,
  thumbnailUrl?: string,
): Promise<ScheduledShowsResponse> {
  return authFetch<ScheduledShowsResponse>(`${getDataApiUrl()}/api/v1/shows/batch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name,
      firstShowAt: firstShowAt.toISOString(),
      thumbnailUrl: thumbnailUrl || null,
      ...recurrence,
    }),
  });
}

export async function getShow(showId: string): Promise<Show> {
  const response = await fetch(`${getDataApiUrl()}/api/v1/shows/${showId}`);
  if (!response.ok) throw new Error('Failed to get show');
  return response.json();
}

export async function startShow(showId: string): Promise<Show> {
  return authFetch<Show>(`${getDataApiUrl()}/api/v1/shows/${showId}/start`, {
    method: 'POST',
  });
}

export async function endShow(showId: string): Promise<Show> {
  return authFetch<Show>(`${getDataApiUrl()}/api/v1/shows/${showId}/end`, {
    method: 'POST',
  });
}

export async function cancelShow(showId: string): Promise<Show> {
  return authFetch<Show>(`${getDataApiUrl()}/api/v1/shows/${showId}/cancel`, {
    method: 'POST',
  });
}

export async function updateSellerNotes(showId: string, sellerNotes: string): Promise<Show> {
  return authFetch<Show>(`${getDataApiUrl()}/api/v1/shows/${showId}/seller-notes`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sellerNotes }),
  });
}

export async function getHmsToken(showId: string): Promise<HmsTokenResponse> {
  return authFetch<HmsTokenResponse>(
    `${getDataApiUrl()}/api/v1/shows/${showId}/hms-token`
  );
}

export async function getPublicHmsViewerToken(showId: string): Promise<HmsTokenResponse> {
  const response = await fetch(`${getDataApiUrl()}/api/v1/shows/${showId}/hms-viewer-token`);
  if (!response.ok) throw new Error('Failed to get viewer token');
  return response.json();
}

// Create auction via Java (which then notifies Go to start it)
export async function createAuction(
  showId: string,
  itemId: string,
  startingPrice: number,
  auctionTimeSeconds: number = 60,
  counterbidTimeSeconds: number = 15,
  suddenDeathEnabled: boolean = true
): Promise<{ id: string }> {
  return authFetch<{ id: string }>(`${getDataApiUrl()}/api/v1/shows/${showId}/auctions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      itemId,
      startingPrice,
      auctionTimeSeconds,
      counterbidTimeSeconds,
      suddenDeathEnabled,
    }),
  });
}

// Realtime Engine API (Go) - for putting item on deck without starting auction
export async function putItemOnDeck(showId: string, item: Item): Promise<void> {
  // The realtime engine only admits the show's seller (bearer token) on its control API.
  await authFetch<void>(`${getRealtimeUrl()}/api/v1/shows/${showId}/on-deck`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id: item.id,
      name: item.name,
      description: item.description,
      imageUrl: item.imageUrl,
      startingPrice: item.startingPrice,
      weightOz: item.weightOz || 16.0,
    }),
  });
}

export async function getAuctionState(auctionId: string): Promise<unknown> {
  return authFetch<unknown>(`${getRealtimeUrl()}/api/v1/auctions/${auctionId}/state`);
}

// Item Sales API (individual item transactions)
export async function getSalesByBuyer(): Promise<ItemSaleWithPayment[]> {
  return authFetch<ItemSaleWithPayment[]>(`${getDataApiUrl()}/api/v1/sales/buyer/me`);
}

export async function getSalesBySeller(): Promise<ItemSaleWithPayment[]> {
  return authFetch<ItemSaleWithPayment[]>(`${getDataApiUrl()}/api/v1/sales/seller/me`);
}

export async function getSalesByShow(showId: string): Promise<ItemSaleWithPayment[]> {
  return authFetch<ItemSaleWithPayment[]>(`${getDataApiUrl()}/api/v1/sales/show/${showId}`);
}

export async function getSale(saleId: string): Promise<ItemSale> {
  return authFetch<ItemSale>(`${getDataApiUrl()}/api/v1/sales/${saleId}`);
}

// Get all shows for the authenticated seller with sale summaries (single request)
export interface SellerShow extends Show {
  saleCount: number;
  saleVolume: number;
}

export async function getMySellerShows(): Promise<SellerShow[]> {
  return authFetch<SellerShow[]>(`${getDataApiUrl()}/api/v1/shows/seller/me`);
}

// List shows (public endpoint, sends auth token if available to filter blocked/banned)
export async function listShows(status?: 'LIVE' | 'SCHEDULED' | 'ENDED', sellerId?: string): Promise<Show[]> {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (sellerId) params.append('sellerId', sellerId);
  const query = params.toString();
  const url = query ? `${getDataApiUrl()}/api/v1/shows?${query}` : `${getDataApiUrl()}/api/v1/shows`;
  const headers: Record<string, string> = {};
  const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
  if (token && !isTokenExpired(token, 0)) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const response = await fetch(url, { headers });
  if (!response.ok) throw new Error('Failed to list shows');
  return response.json();
}

// Listings API
export async function createListing(request: CreateListingRequest): Promise<Listing> {
  return authFetch<Listing>(`${getDataApiUrl()}/api/v1/listings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
}

export async function listListings(includeArchived: boolean = false): Promise<Listing[]> {
  return authFetch<Listing[]>(`${getDataApiUrl()}/api/v1/listings?includeArchived=${includeArchived}`);
}

export async function getListing(listingId: string): Promise<Listing> {
  return authFetch<Listing>(`${getDataApiUrl()}/api/v1/listings/${listingId}`);
}

export async function updateListing(listingId: string, request: UpdateListingRequest): Promise<Listing> {
  return authFetch<Listing>(`${getDataApiUrl()}/api/v1/listings/${listingId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
}

export async function archiveListing(listingId: string): Promise<Listing> {
  return authFetch<Listing>(`${getDataApiUrl()}/api/v1/listings/${listingId}/archive`, {
    method: 'POST',
  });
}

export async function restoreListing(listingId: string): Promise<Listing> {
  return authFetch<Listing>(`${getDataApiUrl()}/api/v1/listings/${listingId}/restore`, {
    method: 'POST',
  });
}

export async function duplicateListing(listingId: string): Promise<Listing> {
  return authFetch<Listing>(`${getDataApiUrl()}/api/v1/listings/${listingId}/duplicate`, {
    method: 'POST',
  });
}

// Create item from listing
export async function createItemFromListing(showId: string, request: CreateItemFromListingRequest): Promise<Item> {
  return authFetch<Item>(`${getDataApiUrl()}/api/v1/shows/${showId}/items/from-listing`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
}

// Reset unsold item back to ON_DECK
export async function resetUnsoldItem(showId: string, itemId: string): Promise<Item> {
  return authFetch<Item>(`${getDataApiUrl()}/api/v1/shows/${showId}/items/${itemId}/reset`, {
    method: 'POST',
  });
}

// Delete item from show
export async function deleteItem(showId: string, itemId: string): Promise<void> {
  await authFetch<void>(`${getDataApiUrl()}/api/v1/shows/${showId}/items/${itemId}`, {
    method: 'DELETE',
  });
}

// Shipping Config API
export async function getShippingConfig(): Promise<ShippingConfig> {
  return authFetch<ShippingConfig>(`${getDataApiUrl()}/api/v1/shipping/config`);
}

export async function updateShippingConfig(config: UpdateShippingConfigRequest): Promise<ShippingConfig> {
  return authFetch<ShippingConfig>(`${getDataApiUrl()}/api/v1/shipping/config`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config),
  });
}

export async function getCarrierServices(carrier?: string): Promise<CarrierServiceOption[]> {
  const params = carrier ? `?carrier=${carrier}` : '';
  return authFetch<CarrierServiceOption[]>(`${getDataApiUrl()}/api/v1/shipping/config/carrier-services${params}`);
}

export async function refreshShippingRates(): Promise<ShippingConfig> {
  return authFetch<ShippingConfig>(`${getDataApiUrl()}/api/v1/shipping/config/refresh-rates`, {
    method: 'POST',
  });
}

export interface TestRateRequest {
  originZip: string;
  destZip: string;
  weightOz: number;
  carrier: string;
  carrierService: string;
}

export interface TestRateResponse {
  rate: number | null;
  carrier: string;
  service: string;
  matched: boolean;
  allRates: Array<{ carrier: string; service: string; rate: number }>;
}

export async function testShippingRate(request: TestRateRequest): Promise<TestRateResponse> {
  return authFetch<TestRateResponse>(`${getDataApiUrl()}/api/v1/shipping/config/test-rate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
}

export async function verifyOriginAddress(address: { street1: string; street2?: string; city: string; state: string; zip: string }): Promise<AddressVerificationResponse> {
  return authFetch<AddressVerificationResponse>(`${getDataApiUrl()}/api/v1/shipping/config/verify-address`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(address),
  });
}

// Buyer Addresses API
export async function getAddresses(): Promise<BuyerAddress[]> {
  return authFetch<BuyerAddress[]>(`${getDataApiUrl()}/api/v1/addresses`);
}

export async function getAddress(addressId: string): Promise<BuyerAddress> {
  return authFetch<BuyerAddress>(`${getDataApiUrl()}/api/v1/addresses/${addressId}`);
}

export async function getDefaultAddress(): Promise<BuyerAddress | null> {
  try {
    return await authFetch<BuyerAddress>(`${getDataApiUrl()}/api/v1/addresses/default`);
  } catch {
    return null;
  }
}

export async function createAddress(address: CreateBuyerAddressRequest): Promise<BuyerAddress> {
  return authFetch<BuyerAddress>(`${getDataApiUrl()}/api/v1/addresses`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(address),
  });
}

export async function updateAddress(addressId: string, address: UpdateBuyerAddressRequest): Promise<BuyerAddress> {
  return authFetch<BuyerAddress>(`${getDataApiUrl()}/api/v1/addresses/${addressId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(address),
  });
}

export async function deleteAddress(addressId: string): Promise<void> {
  await authFetch<void>(`${getDataApiUrl()}/api/v1/addresses/${addressId}`, {
    method: 'DELETE',
  });
}

export interface AddressVerificationResponse {
  matched: boolean;
  original: { streetLine1: string; streetLine2: string | null; city: string; state: string; zipCode: string; country: string };
  suggested: { streetLine1: string; streetLine2: string | null; city: string; state: string; zipCode: string; country: string };
}

export async function verifyAddress(address: CreateBuyerAddressRequest): Promise<AddressVerificationResponse> {
  return authFetch<AddressVerificationResponse>(`${getDataApiUrl()}/api/v1/addresses/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(address),
  });
}

export async function setDefaultAddress(addressId: string): Promise<BuyerAddress> {
  return authFetch<BuyerAddress>(`${getDataApiUrl()}/api/v1/addresses/${addressId}/default`, {
    method: 'POST',
  });
}

// Show buyer totals
export interface ShowBuyerTotals {
  totalItemSpend: number;
  totalShippingPaid: number;
  itemSaleCount: number;
}

export async function getMyShowTotals(showId: string): Promise<ShowBuyerTotals> {
  return authFetch<ShowBuyerTotals>(`${getDataApiUrl()}/api/v1/sales/show/${showId}/my-totals`);
}

// Buy It Now API
export async function createBinSale(showId: string, request: CreateBinSaleRequest): Promise<BinSale> {
  return authFetch<BinSale>(`${getDataApiUrl()}/api/v1/shows/${showId}/bin-sales`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
}

export async function startBinSale(showId: string, saleId: string): Promise<BinSale> {
  return authFetch<BinSale>(`${getDataApiUrl()}/api/v1/shows/${showId}/bin-sales/${saleId}/start`, {
    method: 'POST',
  });
}

export async function endBinSale(showId: string, saleId: string): Promise<BinSale> {
  return authFetch<BinSale>(`${getDataApiUrl()}/api/v1/shows/${showId}/bin-sales/${saleId}/end`, {
    method: 'POST',
  });
}

export async function getActiveBinSale(showId: string): Promise<BinSale | null> {
  try {
    return await authFetch<BinSale>(`${getDataApiUrl()}/api/v1/shows/${showId}/bin-sales/active`);
  } catch {
    return null;
  }
}

export async function getBinSale(saleId: string): Promise<BinSale> {
  return authFetch<BinSale>(`${getDataApiUrl()}/api/v1/bin-sales/${saleId}`);
}

// Payment Methods API
export async function createSetupIntent(): Promise<SetupIntentResponse> {
  return authFetch<SetupIntentResponse>(`${getDataApiUrl()}/api/v1/payment-methods/setup-intent`, {
    method: 'POST',
  });
}

export async function savePaymentMethod(stripePaymentMethodId: string): Promise<PaymentMethod> {
  return authFetch<PaymentMethod>(`${getDataApiUrl()}/api/v1/payment-methods`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ stripePaymentMethodId }),
  });
}

export async function getPaymentMethods(): Promise<PaymentMethod[]> {
  return authFetch<PaymentMethod[]>(`${getDataApiUrl()}/api/v1/payment-methods`);
}

export async function deletePaymentMethod(paymentMethodId: string): Promise<void> {
  await authFetch<void>(`${getDataApiUrl()}/api/v1/payment-methods/${paymentMethodId}`, {
    method: 'DELETE',
  });
}

export async function setDefaultPaymentMethod(paymentMethodId: string): Promise<PaymentMethod> {
  return authFetch<PaymentMethod>(`${getDataApiUrl()}/api/v1/payment-methods/${paymentMethodId}/default`, {
    method: 'POST',
  });
}

export async function checkHasPaymentMethod(): Promise<{ hasPaymentMethod: boolean }> {
  return authFetch<{ hasPaymentMethod: boolean }>(`${getDataApiUrl()}/api/v1/payment-methods/check`);
}

// Stripe Connect API (Seller)
export async function startStripeOnboarding(): Promise<StripeOnboardingResponse> {
  return authFetch<StripeOnboardingResponse>(`${getDataApiUrl()}/api/v1/stripe/connect/onboard`, {
    method: 'POST',
  });
}

export async function getStripeConnectStatus(): Promise<StripeConnectStatus> {
  return authFetch<StripeConnectStatus>(`${getDataApiUrl()}/api/v1/stripe/connect/status`);
}

export async function getStripeDashboardLink(): Promise<StripeDashboardLinkResponse> {
  return authFetch<StripeDashboardLinkResponse>(`${getDataApiUrl()}/api/v1/stripe/connect/dashboard-link`, {
    method: 'POST',
  });
}

// Seller Ledger API
export async function getSellerLedgerEntries(page: number = 0, size: number = 20): Promise<{ content: SellerLedgerEntry[]; totalElements: number; totalPages: number }> {
  return authFetch<{ content: SellerLedgerEntry[]; totalElements: number; totalPages: number }>(
    `${getDataApiUrl()}/api/v1/seller/ledger?page=${page}&size=${size}`
  );
}

export async function getSellerBalanceSummary(): Promise<SellerBalanceSummary> {
  return authFetch<SellerBalanceSummary>(`${getDataApiUrl()}/api/v1/seller/ledger/summary`);
}

export interface BalanceSummary {
  pendingBalance: number;
  availableBalance: number;
  totalBalance: number;
}

export async function getBalanceSummary(): Promise<BalanceSummary> {
  return authFetch<BalanceSummary>(`${getDataApiUrl()}/api/v1/balance`);
}

export interface LedgerEntryResponse {
  id: string;
  entryType: 'CREDIT' | 'DEBIT';
  origin: string;
  amount: number;
  status: string;
  description: string;
  createdAt: string;
}

export async function getBalanceHistory(page: number = 0, size: number = 20): Promise<{ content: LedgerEntryResponse[]; totalPages: number }> {
  return authFetch<{ content: LedgerEntryResponse[]; totalPages: number }>(
    `${getDataApiUrl()}/api/v1/balance/history?page=${page}&size=${size}`
  );
}

export async function setBalanceAutoApply(enabled: boolean): Promise<{ enabled: boolean }> {
  return authFetch<{ enabled: boolean }>(`${getDataApiUrl()}/api/v1/balance/auto-apply`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  });
}

export async function redeemPromoCode(code: string): Promise<BalanceSummary> {
  return authFetch<BalanceSummary>(`${getDataApiUrl()}/api/v1/balance/redeem`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
}

// Seller Payouts API
export async function createPayout(amount: number): Promise<SellerPayout> {
  return authFetch<SellerPayout>(`${getDataApiUrl()}/api/v1/seller/payouts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ amount }),
  });
}

export async function getSellerPayouts(page: number = 0, size: number = 20): Promise<{ content: SellerPayout[]; totalElements: number; totalPages: number }> {
  return authFetch<{ content: SellerPayout[]; totalElements: number; totalPages: number }>(
    `${getDataApiUrl()}/api/v1/seller/payouts?page=${page}&size=${size}`
  );
}

// Item Sale Payment Actions
export async function retrySalePayment(saleId: string, paymentMethodId?: string): Promise<ItemSaleWithPayment> {
  return authFetch<ItemSaleWithPayment>(`${getDataApiUrl()}/api/v1/sales/${saleId}/retry-payment`, {
    method: 'POST',
    ...(paymentMethodId ? {
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paymentMethodId }),
    } : {}),
  });
}

export async function refundSale(saleId: string, amount?: number): Promise<ItemSaleWithPayment> {
  return authFetch<ItemSaleWithPayment>(`${getDataApiUrl()}/api/v1/sales/${saleId}/refund`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(amount ? { amount } : {}),
  });
}

export async function cancelSale(saleId: string): Promise<ItemSaleWithPayment> {
  return authFetch<ItemSaleWithPayment>(`${getDataApiUrl()}/api/v1/sales/${saleId}/cancel`, {
    method: 'POST',
  });
}

// Image Upload API
export interface PresignedUrlResponse {
  uploadUrl: string;
  publicUrl: string;
  maxFileSize: number;
}

export async function getPresignedUploadUrl(
  folder: 'listings' | 'shows' | 'avatars',
  contentType: string,
  filename: string
): Promise<PresignedUrlResponse> {
  return authFetch<PresignedUrlResponse>(`${getDataApiUrl()}/api/v1/uploads/presigned-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ folder, contentType, filename }),
  });
}

export async function uploadImageToR2(file: File, folder: 'listings' | 'shows' | 'avatars'): Promise<string> {
  // 1. Get presigned URL
  const { uploadUrl, publicUrl, maxFileSize } = await getPresignedUploadUrl(
    folder,
    file.type,
    file.name
  );

  // 2. Validate file size
  if (file.size > maxFileSize) {
    throw new Error(`File too large. Maximum size is ${Math.round(maxFileSize / 1024 / 1024)}MB`);
  }

  // 3. Upload directly to R2
  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
    },
    body: file,
  });

  if (!uploadResponse.ok) {
    throw new Error('Failed to upload image');
  }

  // 4. Return public URL
  return publicUrl;
}

export async function getOrdersByShow(showId: string): Promise<Order[]> {
  return authFetch<Order[]>(`${getDataApiUrl()}/api/v1/orders/show/${showId}`);
}

export async function getMyOrderForShow(showId: string): Promise<Order | null> {
  try {
    return await authFetch<Order>(`${getDataApiUrl()}/api/v1/orders/show/${showId}/mine`);
  } catch (err: any) {
    if (err?.status === 404) return null;
    throw err;
  }
}

// Orders API — buyer
export async function getOrdersByBuyer(page: number = 0, size: number = 20, status?: OrderStatus): Promise<{ content: Order[]; totalElements: number; totalPages: number }> {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  if (status) params.set('status', status);
  return authFetch<{ content: Order[]; totalElements: number; totalPages: number }>(
    `${getDataApiUrl()}/api/v1/orders/buyer/me?${params}`
  );
}

// Orders API (fulfillment groups)
export async function getOrdersBySeller(page: number = 0, size: number = 20, status?: OrderStatus, paymentFailed?: boolean): Promise<{ content: Order[]; totalElements: number; totalPages: number }> {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  if (paymentFailed) {
    params.set('paymentFailed', 'true');
  } else if (status) {
    params.set('status', status);
  }
  return authFetch<{ content: Order[]; totalElements: number; totalPages: number }>(
    `${getDataApiUrl()}/api/v1/orders/seller/me?${params}`
  );
}

export async function getOrder(orderId: string): Promise<Order> {
  return authFetch<Order>(`${getDataApiUrl()}/api/v1/orders/${orderId}`);
}

export async function downloadPickList(orderId: string): Promise<Blob> {
  const token = await ensureValidToken();
  if (!token) {
    throw new Error('Session expired. Please log in again.');
  }

  const response = await fetch(`${getDataApiUrl()}/api/v1/orders/${orderId}/pick-list`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to download pick list');
  }

  return response.blob();
}

export async function downloadBulkPickLists(request: {
  orderIds?: string[];
  status?: string;
}): Promise<Blob> {
  const token = await ensureValidToken();
  if (!token) {
    throw new Error('Session expired. Please log in again.');
  }

  const response = await fetch(`${getDataApiUrl()}/api/v1/orders/bulk/pick-lists`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error('Failed to download pick lists');
  }

  return response.blob();
}

// Shipments API
export async function createShipment(orderId: string, itemSaleIds?: string[]): Promise<Shipment> {
  return authFetch<Shipment>(`${getDataApiUrl()}/api/v1/orders/${orderId}/shipments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(itemSaleIds ? { itemSaleIds } : {}),
  });
}

export async function getShipmentsByOrder(orderId: string): Promise<Shipment[]> {
  return authFetch<Shipment[]>(`${getDataApiUrl()}/api/v1/orders/${orderId}/shipments`);
}

export async function getUnshippedItems(orderId: string): Promise<ItemSaleWithPayment[]> {
  return authFetch<ItemSaleWithPayment[]>(`${getDataApiUrl()}/api/v1/orders/${orderId}/unshipped-items`);
}

export async function getShipmentsBySeller(page: number = 0, size: number = 20, status?: ShipmentStatus): Promise<{ content: Shipment[]; totalElements: number; totalPages: number }> {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  if (status) params.set('status', status);
  return authFetch<{ content: Shipment[]; totalElements: number; totalPages: number }>(
    `${getDataApiUrl()}/api/v1/shipments/seller/me?${params}`
  );
}

export async function getShipment(shipmentId: string): Promise<Shipment> {
  return authFetch<Shipment>(`${getDataApiUrl()}/api/v1/shipments/${shipmentId}`);
}

export async function updateShipmentItems(shipmentId: string, itemSaleIds: string[]): Promise<Shipment> {
  return authFetch<Shipment>(`${getDataApiUrl()}/api/v1/shipments/${shipmentId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ itemSaleIds }),
  });
}

export async function getShipmentRates(
  shipmentId: string,
  data: { weightOz: number; lengthIn?: number; widthIn?: number; heightIn?: number; carrierPackageId?: number }
): Promise<GetRatesResponse> {
  return authFetch<GetRatesResponse>(`${getDataApiUrl()}/api/v1/shipments/${shipmentId}/rates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function getCarrierPackages(): Promise<CarrierPackage[]> {
  return authFetch<CarrierPackage[]>(`${getDataApiUrl()}/api/v1/shipping/config/carrier-packages`);
}

export async function buyShipmentLabel(
  shipmentId: string,
  data: { rateId: string }
): Promise<BuyLabelResponse> {
  return authFetch<BuyLabelResponse>(`${getDataApiUrl()}/api/v1/shipments/${shipmentId}/buy-label`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function voidShipmentLabel(shipmentId: string): Promise<Shipment> {
  return authFetch<Shipment>(`${getDataApiUrl()}/api/v1/shipments/${shipmentId}/void-label`, {
    method: 'POST',
  });
}

export async function updateShipmentTracking(shipmentId: string, request: UpdateTrackingRequest): Promise<Shipment> {
  return authFetch<Shipment>(`${getDataApiUrl()}/api/v1/shipments/${shipmentId}/tracking`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
}


export async function deleteShipment(shipmentId: string): Promise<void> {
  const token = await ensureValidToken();
  if (!token) throw new Error('Session expired. Please log in again.');
  const response = await fetch(`${getDataApiUrl()}/api/v1/shipments/${shipmentId}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.message || `Failed to delete shipment (${response.status})`);
  }
}

export async function removeShipmentItem(shipmentId: string, itemSaleId: string): Promise<Shipment | null> {
  const token = await ensureValidToken();
  if (!token) throw new Error('Session expired. Please log in again.');
  const response = await fetch(`${getDataApiUrl()}/api/v1/shipments/${shipmentId}/items/${itemSaleId}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (response.status === 204) return null;
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.message || `Failed to remove item (${response.status})`);
  }
  return response.json();
}

// Chat API (Stream)
export async function getStreamApiKey(): Promise<{ apiKey: string }> {
  const response = await fetch(`${getDataApiUrl()}/api/v1/chat/api-key`);
  if (!response.ok) throw new Error('Failed to get Stream API key');
  return response.json();
}

export async function getChatToken(): Promise<{ token: string; apiKey: string; userId: string }> {
  return authFetch<{ token: string; apiKey: string; userId: string }>(`${getDataApiUrl()}/api/v1/chat/token`, {
    method: 'POST',
  });
}

export async function joinShowChat(showId: string): Promise<void> {
  await authFetch(`${getDataApiUrl()}/api/v1/chat/show/${showId}/join`, {
    method: 'POST',
  });
}

export interface DmRoomInfo {
  channelId: string;
  participantId: string;
  participantUsername: string | null;
  participantAvatarUrl: string | null;
}

export async function getMyDmRooms(): Promise<DmRoomInfo[]> {
  return authFetch<DmRoomInfo[]>(`${getDataApiUrl()}/api/v1/chat/dm/rooms`);
}

export async function getOrCreateDmRoom(userId: string): Promise<{ channelId: string }> {
  return authFetch<{ channelId: string }>(`${getDataApiUrl()}/api/v1/chat/dm/${userId}`, {
    method: 'POST',
  });
}

export async function getFriends(): Promise<any[]> {
  return authFetch<any[]>(`${getDataApiUrl()}/api/v1/friends`);
}

export async function getIncomingFriendRequests(): Promise<any[]> {
  return authFetch<any[]>(`${getDataApiUrl()}/api/v1/friends/requests`);
}

export async function sendFriendRequest(userId: string): Promise<void> {
  await authFetch<any>(`${getDataApiUrl()}/api/v1/friends/request/${userId}`, { method: 'POST' });
}

export async function acceptFriendRequest(userId: string): Promise<void> {
  await authFetch<any>(`${getDataApiUrl()}/api/v1/friends/accept/${userId}`, { method: 'POST' });
}

export async function declineFriendRequest(userId: string): Promise<void> {
  await authFetch<any>(`${getDataApiUrl()}/api/v1/friends/decline/${userId}`, { method: 'POST' });
}

export async function removeFriend(userId: string): Promise<void> {
  await authFetch<void>(`${getDataApiUrl()}/api/v1/friends/${userId}`, { method: 'DELETE' });
}

export async function blockUser(userId: string): Promise<void> {
  await authFetch<void>(`${getDataApiUrl()}/api/v1/blocks/${userId}`, { method: 'POST' });
}

export async function unblockUser(userId: string): Promise<void> {
  await authFetch<void>(`${getDataApiUrl()}/api/v1/blocks/${userId}`, { method: 'DELETE' });
}

export async function getBlockedUsers(): Promise<any[]> {
  return authFetch<any[]>(`${getDataApiUrl()}/api/v1/blocks`);
}

export interface MutedUser {
  id: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  mutedAt: string;
}

export async function getMutedUsers(): Promise<MutedUser[]> {
  return authFetch<MutedUser[]>(`${getDataApiUrl()}/api/v1/mutes`);
}

export async function muteUser(userId: string): Promise<void> {
  await authFetch<void>(`${getDataApiUrl()}/api/v1/mutes/${userId}`, { method: 'POST' });
}

export async function unmuteUser(userId: string): Promise<void> {
  await authFetch<void>(`${getDataApiUrl()}/api/v1/mutes/${userId}`, { method: 'DELETE' });
}

export type ReportTargetType = 'USER' | 'SHOW' | 'CHAT_MESSAGE' | 'ITEM';
export type ReportReason =
  | 'SPAM'
  | 'HARASSMENT'
  | 'HATE_SPEECH'
  | 'EXPLICIT_CONTENT'
  | 'VIOLENCE'
  | 'FRAUD'
  | 'PROHIBITED_ITEM'
  | 'IMPERSONATION'
  | 'OTHER';

export async function submitReport(input: {
  targetType: ReportTargetType;
  targetId: string;
  reason: ReportReason;
  detail?: string;
}): Promise<void> {
  await authFetch<any>(`${getDataApiUrl()}/api/v1/reports`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// Show Moderation API (seller bans & kicks)
export interface BannedUser {
  userId: string;
  username: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  reason: string | null;
  createdAt: string;
}

export async function kickFromShow(showId: string, userId: string): Promise<void> {
  await authFetch<void>(`${getDataApiUrl()}/api/v1/shows/${showId}/kick/${userId}`, {
    method: 'POST',
  });
}

export async function banUser(userId: string, reason?: string): Promise<void> {
  await authFetch<void>(`${getDataApiUrl()}/api/v1/seller/bans/${userId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: reason || undefined }),
  });
}

export async function unbanUser(userId: string): Promise<void> {
  await authFetch<void>(`${getDataApiUrl()}/api/v1/seller/bans/${userId}`, {
    method: 'DELETE',
  });
}

export async function getBannedUsers(page: number = 0): Promise<{ content: BannedUser[] }> {
  return authFetch<{ content: BannedUser[] }>(`${getDataApiUrl()}/api/v1/seller/bans?page=${page}`);
}

export async function checkUserBanned(userId: string): Promise<boolean> {
  try {
    await authFetch<void>(`${getDataApiUrl()}/api/v1/seller/bans/${userId}`);
    return true;
  } catch {
    return false;
  }
}

// Profile API
export async function updateProfile(request: UpdateProfileRequest): Promise<User> {
  return authFetch<User>(`${getAuthApiUrl()}/api/v1/users/me`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
}

export interface UserSearchResult {
  id: string;
  username: string;
  avatarUrl: string | null;
  isSeller: boolean;
}

export async function searchUsers(q: string, limit = 10): Promise<UserSearchResult[]> {
  if (q.trim().length < 2) return [];
  return authFetch<UserSearchResult[]>(
    `${getDataApiUrl()}/api/v1/users/search?q=${encodeURIComponent(q.trim())}&limit=${limit}`
  );
}

export async function getPublicProfile(username: string): Promise<User> {
  const headers: Record<string, string> = {};
  const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
  if (token && !isTokenExpired(token, 0)) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const response = await fetch(`${getDataApiUrl()}/api/v1/users/by-username/${username}`, { headers });
  if (!response.ok) {
    throw new Error('User not found');
  }
  return response.json();
}

export async function getUserByPhone(phoneNumber: string): Promise<User> {
  return authFetch<User>(`${getDataApiUrl()}/api/v1/users/by-phone/${encodeURIComponent(phoneNumber)}`);
}

export async function followUser(userId: string): Promise<{ following: boolean; followerCount: number }> {
  return authFetch<{ following: boolean; followerCount: number }>(`${getDataApiUrl()}/api/v1/follows/${userId}`, {
    method: 'POST',
  });
}

export async function unfollowUser(userId: string): Promise<{ following: boolean; followerCount: number }> {
  return authFetch<{ following: boolean; followerCount: number }>(`${getDataApiUrl()}/api/v1/follows/${userId}`, {
    method: 'DELETE',
  });
}

// Notifications API
export async function getNotifications(page: number = 0, size: number = 20): Promise<NotificationsPage> {
  return authFetch<NotificationsPage>(`${getDataApiUrl()}/api/v1/notifications?page=${page}&size=${size}`);
}

export async function getUnreadNotificationCount(): Promise<number> {
  const response = await authFetch<{ count: number }>(`${getDataApiUrl()}/api/v1/notifications/unread-count`);
  return response.count;
}

export async function markNotificationAsRead(id: string): Promise<void> {
  await authFetch<void>(`${getDataApiUrl()}/api/v1/notifications/${id}/read`, { method: 'POST' });
}

export async function markAllNotificationsAsRead(): Promise<void> {
  await authFetch<void>(`${getDataApiUrl()}/api/v1/notifications/read-all`, { method: 'POST' });
}

// Giveaway API
export async function startGiveaway(showId: string, listingId: string): Promise<Giveaway> {
  return authFetch<Giveaway>(`${getDataApiUrl()}/api/v1/shows/${showId}/giveaways`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ listingId }),
  });
}

export async function drawGiveawayWinner(giveawayId: string): Promise<Giveaway> {
  return authFetch<Giveaway>(`${getDataApiUrl()}/api/v1/giveaways/${giveawayId}/draw`, {
    method: 'POST',
  });
}

export async function cancelGiveaway(giveawayId: string): Promise<Giveaway> {
  return authFetch<Giveaway>(`${getDataApiUrl()}/api/v1/giveaways/${giveawayId}/cancel`, {
    method: 'POST',
  });
}

export async function getGiveaway(giveawayId: string): Promise<Giveaway> {
  return authFetch<Giveaway>(`${getDataApiUrl()}/api/v1/giveaways/${giveawayId}`);
}

// Session type
export interface Session {
  id: string;
  deviceType: string | null;
  deviceName: string | null;
  ipAddress: string | null;
  locationCity: string | null;
  locationCountry: string | null;
  current: boolean;
  createdAt: string;
  lastActiveAt: string | null;
}

// Security API
export async function changePassword(data: { currentPassword: string; newPassword: string; confirmNewPassword: string }): Promise<void> {
  await authFetch<{ message: string }>(`${getAuthApiUrl()}/api/v1/auth/change-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  clearAuthState();
}

export async function getSessions(): Promise<Session[]> {
  return authFetch<Session[]>(`${getAuthApiUrl()}/api/v1/auth/sessions`);
}

export async function revokeSession(sessionId: string): Promise<void> {
  await authFetch<{ message: string }>(`${getAuthApiUrl()}/api/v1/auth/sessions/${sessionId}`, {
    method: 'DELETE',
  });
}

export async function requestEmailChange(data: { newEmail: string }): Promise<void> {
  await authFetch<{ message: string }>(`${getAuthApiUrl()}/api/v1/auth/change-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function verifyEmailChange(data: { code: string }): Promise<void> {
  await authFetch<{ message: string }>(`${getAuthApiUrl()}/api/v1/auth/change-email/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

// Phone Number (for social login users)
export async function setPhoneNumber(phoneNumber: string): Promise<void> {
  await authFetch<{ message: string }>(`${getAuthApiUrl()}/api/v1/auth/verification/phone/set`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phoneNumber }),
  });
}

// Verification API
export interface VerificationStatus {
  phoneVerified: boolean;
  emailVerified: boolean;
}

export async function sendPhoneVerification(): Promise<void> {
  await authFetch<{ message: string }>(`${getAuthApiUrl()}/api/v1/auth/verification/phone/send`, {
    method: 'POST',
  });
}

export async function verifyPhone(code: string): Promise<void> {
  await authFetch<{ message: string }>(`${getAuthApiUrl()}/api/v1/auth/verification/phone/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
}

export async function sendEmailVerification(): Promise<void> {
  await authFetch<{ message: string }>(`${getAuthApiUrl()}/api/v1/auth/verification/email/send`, {
    method: 'POST',
  });
}

export async function verifyEmail(code: string): Promise<void> {
  await authFetch<{ message: string }>(`${getAuthApiUrl()}/api/v1/auth/verification/email/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
}

export async function getVerificationStatus(): Promise<VerificationStatus> {
  return authFetch<VerificationStatus>(`${getAuthApiUrl()}/api/v1/auth/verification/status`);
}

export async function notifyFeedback(category: string, message: string, username: string, email: string): Promise<void> {
  await authFetch(`${getDataApiUrl()}/api/v1/feedback/notify`, {
    method: 'POST',
    body: JSON.stringify({ category, message, username, email }),
  });
}
